// resources/js/Pages/PurchaseOrders/PurchaseOrdersList.tsx
import React, { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';

interface LigneCommande {
  id: string;
  produit: string;
  quantite: number;
  prix_unitaire_ht: number;
}

interface PurchaseOrder {
  id: string;
  numero: string;
  fournisseur: string;
  fournisseur_id?: string;
  emplacement_id?: string;
  date_commande: string;
  date_livraison_prevue: string | null;
  total_ttc: number;
  total_ht: number;
  total_tva: number;
  devise: string;
  statut:
    | 'Brouillon'
    | 'Envoyé'
    | 'En Cours (Partiel)'
    | 'Livrée'
    | 'Annulée'
    | 'Alerte';
  lignes: LigneCommande[];
  motif_annulation?: string;
}

interface PurchaseOrdersListProps {
  onEdit: (order: PurchaseOrder) => void;
  onAdd: () => void;
  onReceive?: (order: PurchaseOrder) => void;
  onViewDetails?: (order: PurchaseOrder) => void;
}

const PurchaseOrdersList: React.FC<PurchaseOrdersListProps> = ({
  onEdit,
  onAdd,
  onReceive,
  onViewDetails,
}) => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');

  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendDate, setSendDate] = useState(new Date().toISOString().split('T')[0]);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Chargement des bons de commande depuis l'API Laravel
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await getResources(
          "bons-commande?with=fournisseur,devise,lignes.produit,utilisateur"
        );

        const statusMap: Record<string, PurchaseOrder["statut"]> = {
          BROUILLON: "Brouillon",
          ENVOYE: "Envoyé",
          PARTIEL: "En Cours (Partiel)",
          LIVRE: "Livrée",
          ANNULE: "Annulée",
          ALERTE: "Alerte",
        };

        const mapped: PurchaseOrder[] = res.data.data.map((bc: any) => ({
          id: bc.id?.toString(),
          numero: bc.numero,

          fournisseur: bc.fournisseur?.nom ?? "—",
          fournisseur_id: bc.fournisseur?.id?.toString(),

          date_commande: bc.date, // 👉 correspond à date_commande formatée
          date_livraison_prevue: bc.date_livraison_prevue,

          total_ttc: Number(bc.total_ttc ?? 0),
          total_ht: Number(bc.total_ht ?? 0),
          total_tva: Number(bc.total_tva ?? 0),

          devise: bc.devise ?? "", // 👉 le backend renvoie déjà un code (ex: "GNF")

          statut: statusMap[bc.statut] ?? "Brouillon",

          lignes: bc.lignes?.map((l: any) => ({
            id: l.id?.toString(),
            produit: l.produit_nom ?? "--",
            produit_id: l.produit_id?.toString(),
            quantite: l.quantite ?? 0,
            stockMax: l.stock_max ?? 0,
            quantite_recue: l.quantite_recue ?? 0,
            stock_actuel: l.stock_actuel ?? 0, // 👉 backend renvoie déjà stock_actuel
            prix_unitaire_ht: Number(l.prix_unitaire_ht ?? 0),
          })) ?? [],

          motif_annulation: bc.notes || undefined,
        }));

        console.log("Bons de commande chargés :", mapped);
        setOrders(mapped);

      } catch (error) {
        console.error(error);
        setError("Impossible de charger les bons de commande");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Mise à jour locale après action
  const updateOrder = (id: string, changes: Partial<PurchaseOrder>) => {
    setOrders(prev => prev.map(o => o.id === id ? { ...o, ...changes } : o));
  };

  const handleSendOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setShowSendModal(true);
  };

  const confirmSendOrder = async () => {
  if (!selectedOrder) return;

  // Vérifier côté front pour éviter l'appel inutile
  if (new Date(sendDate) <= new Date()) {
    alert("La date de livraison prévue doit être supérieure à aujourd'hui.");
    return;
  }

  try {
    await createResource(`bons-commande/${selectedOrder.id}/envoyer`, {
      date_livraison_prevue: sendDate,
    });

    updateOrder(selectedOrder.id, {
      statut: 'Envoyé',
      date_livraison_prevue: sendDate,
    });

    setShowSendModal(false);
    setSelectedOrder(null);

  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.errors?.date_livraison_prevue?.[0] ||
      "Erreur lors de l'envoi du bon de commande";

    alert(message);
  }
};


  const handleCancelOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!selectedOrder || !cancelReason.trim()) {
      alert('Veuillez saisir un motif d\'annulation');
      return;
    }

    try {
      
    await createResource(
      `bons-commande/${selectedOrder.id}/annuler`,
      { motif: cancelReason }
    );

      updateOrder(selectedOrder.id, {
        statut: 'Annulée',
        motif_annulation: cancelReason,
      });

      setShowCancelModal(false);
      setSelectedOrder(null);
      setCancelReason('');
    } catch {
      alert('Erreur lors de l\'annulation');
    }
  };

const handlePrintReceipt = (order: PurchaseOrder) => {
  // Les montants viennent directement du backend → plus fiable
  const totalHT = order.total_ht ?? 0;
  const totalTVA = order.total_tva ?? 0;
  const totalTTC = order.total_ttc ?? 0;

  const printContent = `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>Bon de Commande - ${order.numero}</title>
      <style>
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          margin: 0;
          padding: 40px;
          background: #f9fff9;
          color: #1a3c34;
          line-height: 1.6;
        }
        .container {
          max-width: 900px;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,100,0,0.08);
          overflow: hidden;
        }
        .header {
          background: linear-gradient(135deg, #2e7d32, #4caf50);
          color: white;
          padding: 30px;
          text-align: center;
        }
        .logo {
          height: 80px;
          margin-bottom: 15px;
          filter: brightness(0) invert(1);
        }
        .header h1 {
          margin: 0;
          font-size: 28px;
          font-weight: 600;
        }
        .header h2 {
          margin: 8px 0 0;
          font-size: 22px;
          opacity: 0.95;
          font-weight: 400;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          padding: 30px;
          background: #f0f7f0;
        }
        .info-item {
          display: flex;
          flex-direction: column;
        }
        .info-label {
          font-size: 13px;
          color: #2e7d32;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .info-value {
          font-size: 16px;
          color: #1a3c34;
          font-weight: 500;
        }
        .table-container {
          padding: 30px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 15px;
        }
        th {
          background: #e8f5e9;
          color: #2e7d32;
          font-weight: 600;
          text-align: left;
          padding: 14px 16px;
          border-bottom: 2px solid #4caf50;
        }
        td {
          padding: 14px 16px;
          border-bottom: 1px solid #c8e6c9;
        }
        tr:hover {
          background: #f0f7f0;
        }
        .text-right { text-align: right; }
        .total-section {
          margin-top: 40px;
          float: right;
          width: 420px;
          border: 1px solid #c8e6c9;
          border-radius: 8px;
          overflow: hidden;
        }
        .total-section table {
          width: 100%;
          background: #f8fff8;
          font-size: 16px;
        }
        .total-row {
          font-weight: 600;
        }
        .total-row td {
          padding: 12px 16px;
        }
        .total-row-strong {
          font-size: 18px;
          font-weight: 700;
          background: #4caf50 !important;
          color: white;
        }
        .total-row-strong td {
          padding: 16px;
        }
        .signature {
          margin-top: 80px;
          display: flex;
          justify-content: space-between;
          padding: 0 60px;
          clear: both;
        }
        .signature-line {
          border-top: 2px solid #2e7d32;
          width: 300px;
          padding-top: 12px;
          text-align: center;
          font-weight: 600;
          color: #2e7d32;
          font-size: 15px;
        }
        .footer {
          padding: 30px;
          text-align: center;
          color: #4a7043;
          font-size: 14px;
          border-top: 1px solid #c8e6c9;
          margin-top: 50px;
          clear: both;
        }
        @media print {
          body { padding: 20px; background: white; }
          .container { box-shadow: none; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <img src="https://via.placeholder.com/200x80/2e7d32/ffffff?text=TON+LOGO" alt="Logo Entreprise" class="logo">
          <h1>Bon de Commande Fournisseur</h1>
          <h2>N° ${order.numero}</h2>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">Fournisseur</div>
            <div class="info-value">${order.fournisseur}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Date de commande</div>
            <div class="info-value">${formatDate(order.date_commande)}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Date de livraison prévue</div>
            <div class="info-value">${order.date_livraison_prevue ? formatDate(order.date_livraison_prevue) : 'Non définie'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Devise</div>
            <div class="info-value">${order.devise}</div>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th style="width: 40%">Désignation</th>
                <th class="text-right">Quantité</th>
                <th class="text-right">P.U. HT</th>
                <th class="text-right">Total HT</th>
              </tr>
            </thead>
            <tbody>
              ${order.lignes.map(l => `
                <tr>
                  <td><strong>${l.produit}</strong></td>
                  <td class="text-right">${new Intl.NumberFormat('fr-FR').format(l.quantite)}</td>
                  <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(l.prix_unitaire_ht)}</td>
                  <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(l.quantite * l.prix_unitaire_ht)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <!-- Totaux -->
          <div class="total-section">
            <table>
              <tbody>
                <tr class="total-row">
                  <td>Total HT</td>
                  <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalHT)} ${order.devise}</td>
                </tr>
                <tr class="total-row">
                  <td>Montant TVA</td>
                  <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalTVA)} ${order.devise}</td>
                </tr>
                <tr class="total-row-strong">
                  <td><strong>Total TTC</strong></td>
                  <td class="text-right"><strong>${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalTTC)} ${order.devise}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="signature">
          <div class="signature-line">Cachet et signature du fournisseur</div>
          <div class="signature-line">Émis par (Responsable Achats)</div>
        </div>

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}<br>
          Bon de commande officiel – Référence : ${order.numero}
        </div>
      </div>
    </body>
    </html>
  `;

  const win = window.open('', '_blank', 'width=900,height=800');
  if (win) {
    win.document.write(printContent);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
      // Optionnel : fermer après impression
      // win.close();
    };
  }
};

  const filteredOrders = orders.filter(order => {
    const search = searchTerm.toLowerCase();
    const matchSearch = order.numero.toLowerCase().includes(search) ||
                        order.fournisseur.toLowerCase().includes(search);
    const matchStatus = filterStatus === 'Tous' || order.statut === filterStatus;
    return matchSearch && matchStatus;
  });

  const getStatusColor = (statut: string) => {
    const colors: Record<string, string> = {
      'Brouillon': 'bg-gray-100 text-gray-800',
      'Envoyé': 'bg-blue-100 text-blue-800',
      'En Cours (Partiel)': 'bg-yellow-100 text-yellow-800',
      'Livrée': 'bg-green-100 text-green-800',
      'Annulée': 'bg-red-100 text-red-800',
      'Alerte': 'bg-orange-100 text-orange-800',
    };
    return colors[statut] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (statut: string) => {
    const icons: Record<string, string> = {
      'Brouillon': 'ri-draft-line',
      'Envoyé': 'ri-send-plane-fill',
      'En Cours (Partiel)': 'ri-time-line',
      'Livrée': 'ri-checkbox-circle-fill',
      'Annulée': 'ri-close-circle-fill',
      'Alerte': 'ri-alarm-warning-fill',
    };
    return icons[statut] || 'ri-file-line';
  };

  const formatAmount = (amount: number, devise: string) => {
    if (amount === 0) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + devise;
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const isOverdue = (order: PurchaseOrder) => {
    if (!order.date_livraison_prevue || ['Livrée', 'Annulée'].includes(order.statut)) return false;
    return new Date(order.date_livraison_prevue) < new Date();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-16 text-center">
        <i className="ri-loader-4-line text-5xl text-gray-400 animate-spin"></i>
        <p className="mt-4 text-lg text-gray-600">Chargement des bons de commande...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-700 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Statistiques */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-6">
          {['Brouillon','Envoyé','En Cours (Partiel)','Livrée','Alerte','Annulée'].map(s => (
            <div key={s} className="text-center">
              <div className={`text-2xl font-bold ${
                s === 'Envoyé' ? 'text-blue-600' :
                s === 'En Cours (Partiel)' ? 'text-yellow-600' :
                s === 'Livrée' ? 'text-green-600' :
                s === 'Alerte' ? 'text-orange-600' :
                s === 'Annulée' ? 'text-red-600' : 'text-gray-900'
              }`}>
                {orders.filter(o => o.statut === s).length}
              </div>
              <div className="text-sm text-gray-500">{s === 'En Cours (Partiel)' ? 'En cours' : s + (s === 'Brouillon' ? 's' : 'es')}</div>
            </div>
          ))}
        </div>

        {/* Recherche & Filtre */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Rechercher par numéro ou fournisseur..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="Tous">Tous les statuts</option>
            <option value="Brouillon">Brouillon</option>
            <option value="Envoyé">Envoyé</option>
            <option value="En Cours (Partiel)">En cours</option>
            <option value="Livrée">Livrée</option>
            <option value="Alerte">En alerte</option>
            <option value="Annulée">Annulée</option>
          </select>
        </div>
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bon de Commande</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredOrders.map(order => (
              <tr key={order.id} className={`hover:bg-gray-50 ${isOverdue(order) ? 'bg-red-50' : ''}`}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{order.numero}</div>
                  <div className="text-sm text-gray-500">{order.lignes.length} ligne(s)</div>
                  {isOverdue(order) && <div className="text-xs text-red-600 mt-1">Délai dépassé</div>}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.fournisseur}</td>
                <td className="px-6 py-4 text-sm">
                  <div>Cmd: {formatDate(order.date_commande)}</div>
                  <div className="text-gray-500">Liv: {formatDate(order.date_livraison_prevue)}</div>
                </td>
                <td className="px-6 py-4 text-sm font-medium">
                  {formatAmount(order.total_ttc, order.devise)}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(order.statut)}`}>
                    <i className={getStatusIcon(order.statut)}></i>
                    {order.statut}
                  </span>
                  {order.motif_annulation && (
                    <div className="text-xs text-gray-500 mt-1">
                      Motif: {order.motif_annulation.substring(0, 30)}...
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-3 flex-wrap sm:flex-nowrap">
                    {order.statut === 'Brouillon' && (
                      <>
                        <button onClick={() => onEdit(order)} title="Modifier" className="text-blue-600 hover:text-blue-900">
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button onClick={() => handleSendOrder(order)} title="Envoyer" className="text-purple-600 hover:text-purple-900">
                          <i className="ri-send-plane-line text-lg"></i>
                        </button>
                      </>
                    )}
                    {order.statut !== 'Brouillon' && onViewDetails && (
                      <button onClick={() => onViewDetails(order)} title="Détails" className="text-blue-600 hover:text-blue-900">
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                    )}
                    {(order.statut === 'Envoyé' || order.statut === 'En Cours (Partiel)' || order.statut === 'Alerte') && onReceive && (
                      <button onClick={() => onReceive(order)} title="Réceptionner" className="text-orange-600 hover:text-orange-900">
                        <i className="ri-truck-line text-lg"></i>
                      </button>
                    )}
                    {(order.statut === 'Brouillon' || order.statut === 'Envoyé') && (
                      <button onClick={() => handleCancelOrder(order)} title="Annuler" className="text-red-600 hover:text-red-900">
                        <i className="ri-close-circle-line text-lg"></i>
                      </button>
                    )}
                    {(order.statut === 'Envoyé' || order.statut === 'En Cours (Partiel)') && (
                      <button onClick={() => handlePrintReceipt(order)} title="Imprimer BR" className="text-green-600 hover:text-green-900">
                        <i className="ri-printer-line text-lg"></i>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Aucun résultat */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-16">
          <i className="ri-file-list-3-line text-5xl text-gray-300"></i>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Aucune commande trouvée
          </h3>
          <p className="text-gray-500 mt-2">
            {searchTerm ? 'Aucun résultat pour votre recherche.' : 'Créez votre première commande.'}
          </p>
          <button
            onClick={onAdd}
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            <i className="ri-add-line mr-2"></i> Créer une commande
          </button>
        </div>
      )}

      {/* Modal Envoi */}
      {showSendModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-send-plane-fill text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold">Envoyer le bon de commande</h3>
              <p className="text-gray-600 mt-2">{selectedOrder.numero} - {selectedOrder.fournisseur}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Date de livraison prévue <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={sendDate}
                onChange={e => setSendDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowSendModal(false); setSelectedOrder(null); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmSendOrder}
                className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Annulation */}
      {showCancelModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-close-circle-fill text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold">Annuler le bon de commande</h3>
              <p className="text-gray-600 mt-2">{selectedOrder.numero} - {selectedOrder.fournisseur}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Motif d'annulation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={4}
                placeholder="Ex: changement de besoin, erreur de saisie..."
                className="w-full px-4 py-2 border rounded-lg resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCancelModal(false); setSelectedOrder(null); setCancelReason(''); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                onClick={confirmCancel}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersList;